import { Injectable } from '@angular/core';
import { CollabApiService } from '@portal-core/collab/services/collab-api.service';
import { ProjectFile } from '@portal-core/project-files/models/project-file.model';
import { ProjectFilesTreeDataProvider, ProjectFileTreeUpdate, TreeDataRequest } from '@portal-core/project-files/services/project-files-tree-data-provider';
import { WorkspaceFilesService } from '@portal-core/workspaces/services/workspace-files/workspace-files.service';
import { WorkspaceSessionService } from '@portal-core/workspaces/services/workspace-session.service';
import { Observable, Subject, takeUntil } from 'rxjs';

/**
 * Workspace data provider for the project files tree.
 * Calls the collab-authority /api/workspaces/fileTree endpoint which returns
 * a merged tree of git files + workspace files (with IsUncommitted set on workspace files).
 * Pushes real-time updates via fileUpdates$ when WebSocket events arrive.
 */
@Injectable()
export class WorkspaceFilesDataProvider extends ProjectFilesTreeDataProvider {
  private fileUpdateSubject: Subject<ProjectFileTreeUpdate> = new Subject<ProjectFileTreeUpdate>();
  readonly fileUpdates$: Observable<ProjectFileTreeUpdate> = this.fileUpdateSubject.asObservable();
  private destroy$: Subject<void> = new Subject<void>();
  private workspaceSessionService: WorkspaceSessionService;

  constructor(private collabApiService: CollabApiService, private workspaceFilesService: WorkspaceFilesService) {
    super();
  }

  /**
   * Start listening for WebSocket file events to push real-time tree updates.
   * Must be called after construction when a WorkspaceSessionService is available.
   */
  init(workspaceSessionService: WorkspaceSessionService) {
    this.workspaceSessionService = workspaceSessionService;
    workspaceSessionService.fileInserted$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(file => {
      // The workspace socket only fires for collab files, and the file always has uncommitted
      // changes (it was just inserted into workspace_files).
      this.fileUpdateSubject.next({
        added: [{ filePath: file.filePath, isUncommitted: true, isCollabFile: true }],
        removed: [],
        updated: []
      });
    });

    workspaceSessionService.fileDeleted$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(file => {
      // A workspace file is removed from MongoDB either because the file was newly added and then discarded
      // (gitStatus === 'added' — the file is truly gone) or because an already-tracked file's uncommitted
      // changes were discarded or committed (gitStatus !== 'added' — the file still exists in git, just without the dot).
      if (file.gitStatus === 'added') {
        this.fileUpdateSubject.next({ added: [], removed: [file.filePath], updated: [] });
      } else {
        this.fileUpdateSubject.next({ added: [], removed: [], updated: [{ filePath: file.filePath, isUncommitted: false }] });
      }
    });

    workspaceSessionService.fileRenamed$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(event => {
      // A collab-authority-originated rename. gitStatus === 'added' means the file is still pending add
      // (the rename carries its uncommitted state); anything else means the rename was committed and the
      // new path has no uncommitted changes. Every file flowing through this path is a collab file.
      const isUncommitted = event.gitStatus === 'added';
      this.fileUpdateSubject.next({
        added: [{ filePath: event.newFilePath, isUncommitted, isCollabFile: true }],
        removed: [event.oldFilePath],
        updated: []
      });
    });

    workspaceSessionService.docDeleted$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(event => {
      // A collab-authority-originated explicit delete. Drop the node from the tree regardless of gitStatus —
      // the workspace_file row is gone on the server for both pending-add deletions and committed-file deletions
      // (the committed file now has a pending git deletion, but there's no local state left to represent it).
      this.fileUpdateSubject.next({ added: [], removed: [event.filePath], updated: [] });
    });
  }

  /**
   * Returns true when any workspace file for the current project/branch lives under `folderPath`.
   * Answers authoritatively from `WorkspaceFilesService`, which is kept in sync by the workspace
   * session's websocket events — so this reflects the full project state, not just the loaded tree.
   */
  hasUncommittedDescendant(folderPath: string): boolean {
    if (!this.workspaceSessionService) {
      return false;
    }
    const files = this.workspaceFilesService.getItemsByProperties('projectId', this.workspaceSessionService.projectId, 'branchName', this.workspaceSessionService.branchName);
    const prefix = `${folderPath}/`;
    return files.some(file => file.filePath.startsWith(prefix));
  }

  getTree$(request: TreeDataRequest): Observable<ProjectFile[]> {
    return this.collabApiService.getWorkspaceFileTree$(
      request.projectId,
      request.branchName,
      request.treePath,
      request.includeFiles,
      request.fileFilter,
      request.committedFilter
    );
  }

  dispose() {
    this.destroy$.next();
    this.destroy$.complete();
    this.fileUpdateSubject.complete();
  }
}
